import { useQuery } from '@tanstack/react-query'
import { getStats, getStages } from '../../api/admin'

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow p-6">
      <p className="text-gray-500 text-sm">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  )
}

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: getStats,
  })

  const { data: stages } = useQuery({
    queryKey: ['admin-stages'],
    queryFn: getStages,
  })

  const activeStages = stages?.filter(s => s.is_active).length || 0
  const stagesWithModel = stages?.filter(s => s.model).length || 0

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>

      {/* Stats grid */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={stats?.total_users || 0} color="text-blue-600" />
          <StatCard label="Total Projects" value={stats?.total_projects || 0} color="text-gray-800" />
          <StatCard label="Completed" value={stats?.projects_completed || 0} color="text-green-600" />
          <StatCard label="Processing" value={stats?.projects_processing || 0} color="text-orange-600" />
        </div>
      )}

      {/* Pipeline overview */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Pipeline Overview</h2>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-gray-500 text-sm">Total Stages</p>
              <p className="text-2xl font-bold text-gray-800">{stages?.length || 0}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active Stages</p>
              <p className="text-2xl font-bold text-green-600">{activeStages}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Stages with LLM</p>
              <p className="text-2xl font-bold text-purple-600">{stagesWithModel}</p>
            </div>
          </div>

          {/* Stage list preview */}
          <div className="mt-6 border-t pt-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {stages?.map((stage, index) => (
                <div
                  key={stage.id}
                  className={`flex items-center ${index < (stages?.length || 0) - 1 ? 'flex-shrink-0' : ''}`}
                >
                  <div
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                      stage.is_active
                        ? stage.model
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {stage.name}
                  </div>
                  {index < (stages?.length || 0) - 1 && (
                    <span className="mx-2 text-gray-400">→</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a
            href="/admin/pipelines"
            className="bg-white rounded-xl shadow p-6 hover:shadow-md transition group"
          >
            <div className="flex items-center gap-4">
              <span className="text-3xl">⚙️</span>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition">
                  Configure Pipeline
                </h3>
                <p className="text-gray-500 text-sm">Edit stages, models, and prompts</p>
              </div>
            </div>
          </a>

          <a
            href="/admin/projects"
            className="bg-white rounded-xl shadow p-6 hover:shadow-md transition group"
          >
            <div className="flex items-center gap-4">
              <span className="text-3xl">📁</span>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition">
                  View Projects
                </h3>
                <p className="text-gray-500 text-sm">Monitor processing status</p>
              </div>
            </div>
          </a>

          <a
            href="/admin/models"
            className="bg-white rounded-xl shadow p-6 hover:shadow-md transition group"
          >
            <div className="flex items-center gap-4">
              <span className="text-3xl">🤖</span>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition">
                  Manage Models
                </h3>
                <p className="text-gray-500 text-sm">View and refresh LLM models</p>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Failed projects alert */}
      {stats && stats.projects_failed > 0 && (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <span className="text-3xl">⚠️</span>
            <div>
              <h3 className="font-semibold text-red-800">
                {stats.projects_failed} Failed Project{stats.projects_failed > 1 ? 's' : ''}
              </h3>
              <p className="text-red-600 text-sm">
                Some projects failed processing. Check the projects page for details.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
